import { useEffect } from "react";

export const SITE = {
  name: "Diocese Anglicana Católica do Brasil",
  short: "DACB",
  url: "https://dacbrasil.com.br",
  logo: "https://dacbrasil.com.br/brasao-dacb-oficial.png",
  description:
    "Site oficial da Diocese Anglicana Católica do Brasil — DACB, jurisdição eclesiástica de âmbito nacional integrante da Comunhão Anglicana Livre Internacional, com Sé Primacial em Esmeraldas/MG e presença pastoral em todo o Brasil.",
  slogan: "Fé Católica • Tradição Anglicana • Sucessão Apostólica • Missão no Brasil",
  socials: {
    facebook: "https://www.facebook.com/share/18EWnu4Y4z/",
    instagram: "https://www.instagram.com/dommacieira",
    youtube: "https://youtube.com/@exorcistadedeus",
  },
};

export function orgJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE.url}/#organization`,
        name: SITE.name,
        alternateName: ["DACB", "Anglican Free Communion Brasil", "Comunhão Anglicana Livre Internacional Brasil"],
        url: `${SITE.url}/`,
        logo: SITE.logo,
        description: SITE.description,
        slogan: SITE.slogan,
        memberOf: {
          "@type": "Organization",
          name: "The Anglican Free Communion International",
          alternateName: "Comunhão Anglicana Livre Internacional",
          url: "https://anglicanfreecommunion.uk/",
        },
        telephone: "+55-31-99305-3446",
        email: "chancelaria@dacb.org.br",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Rua 1, nº 650, Bairro Monte Sinai",
          addressLocality: "Esmeraldas",
          addressRegion: "MG",
          addressCountry: "BR",
        },
        sameAs: [SITE.socials.facebook, SITE.socials.instagram, SITE.socials.youtube],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE.url}/#website`,
        name: SITE.name,
        url: `${SITE.url}/`,
        inLanguage: "pt-BR",
        publisher: { "@id": `${SITE.url}/#organization` },
      },
      {
        "@type": "WebPage",
        "@id": `${SITE.url}/#webpage`,
        url: `${SITE.url}/`,
        name: "Diocese Anglicana Católica do Brasil — DACB | Site Oficial",
        description: SITE.description,
        isPartOf: { "@id": `${SITE.url}/#website` },
        about: { "@id": `${SITE.url}/#organization` },
        inLanguage: "pt-BR",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [{ "@type": "ListItem", position: 1, name: "Início", item: `${SITE.url}/` }],
      },
    ],
  };
}

function setMeta(attr, key, content) {
  const found = document.head.querySelectorAll(`meta[${attr}="${key}"]`);
  found.forEach((el, i) => {
    if (i > 0) el.remove();
  });
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setTitle(text) {
  const titles = document.head.querySelectorAll("title");
  titles.forEach((el, i) => {
    if (i > 0) el.remove();
  });
  let titleEl = document.head.querySelector("title");
  if (!titleEl) {
    titleEl = document.createElement("title");
    document.head.appendChild(titleEl);
  }
  titleEl.textContent = text;
}

// Head gerenciado de forma DETERMINISTICA (fonte unica por tag): as tags
// estaticas do HTML sao atualizadas in-place em vez de duplicadas. O JSON-LD
// estatico do pre-render e removido ao montar e substituido por um unico
// script gerenciado (data-seo-jsonld).
export default function Seo({ title, description, path = "/", image, type = "website", jsonLd }) {
  const fullTitle = title
    ? `${title} | Diocese Anglicana Católica do Brasil – DACB`
    : "Diocese Anglicana Católica do Brasil — DACB | Site Oficial";
  const desc = description || SITE.description;
  const url = `${SITE.url}${path}`;
  const img = image || SITE.logo;

  useEffect(() => {
    setTitle(fullTitle);
    setMeta("name", "description", desc);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", img);
    setMeta("property", "og:locale", "pt_BR");
    setMeta("property", "og:site_name", SITE.name);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);

    const canonicals = document.head.querySelectorAll('link[rel="canonical"]');
    canonicals.forEach((el, i) => {
      if (i > 0) el.remove();
    });
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    document.head.querySelectorAll("script[data-prerender-jsonld]").forEach((el) => el.remove());
    let ld = document.head.querySelector("script[data-seo-jsonld]");
    if (!jsonLd) {
      if (ld) ld.remove();
      return;
    }
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seo-jsonld", "1");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(jsonLd);
  }, [fullTitle, desc, url, img, type, jsonLd]);

  return null;
}

// Versao minima para paginas internas que so precisam de titulo/robots (ex.: admin).
export function useSimpleHead(title, robots) {
  useEffect(() => {
    setTitle(title);
    if (robots) setMeta("name", "robots", robots);
  }, [title, robots]);
}
